import {
    FormValidator,
    isRequired,
    maxLengthOf,
    validateEmail,
    validateLowerCase,
    validateNumber,
    validateSymbols,
    validateUppercase,
} from "../ReduxForm/validators"
import {i18n} from "../../i18n/i18n"

type ValidationRuleSet<T = Record<string, unknown>> = FormValidator<T>[]

interface EmailValidationRules {
    createOrEdit: ValidationRuleSet
    use: ValidationRuleSet
}

interface PasswordValidationRules {
    create: ValidationRuleSet
    edit: ValidationRuleSet
    use: ValidationRuleSet
    repeatIfPasswordProvided: (passwordField?: string) => ValidationRuleSet
    repeatRequired: (passwordField?: string) => ValidationRuleSet
}

const minLengthOf8: FormValidator = (value) => {
    if (value?.length && value.length < 8) {
        return i18n.t("person:validation.password.characters_min_length_other", {count: 8})
    }
}

export const validatePasswordRepeat = (passwordField: string = "password"): FormValidator => {
    return (value, formValues) => {
        const match = formValues?.[passwordField]
        if (value !== match) {
            return i18n.t("person:validation.password.passwords_match")
        }
    }
}

const isRequiredIfFieldExists = (fieldName: string): FormValidator => {
    return (value, formValues) => {
        const related = formValues?.[fieldName]
        return related ? isRequired(value) : undefined
    }
}

export const emailValidationRules: EmailValidationRules = {
    createOrEdit: [validateEmail, maxLengthOf(100)],
    use: [validateEmail],
}

const basePasswordRules: ValidationRuleSet = [
    minLengthOf8,
    maxLengthOf(100),
    validateSymbols,
    validateNumber,
    validateUppercase,
    validateLowerCase,
]

export const passwordValidationRules: PasswordValidationRules = {
    create: [...basePasswordRules, isRequired],
    edit: [...basePasswordRules],
    use: [isRequired],
    /** Memoize before passing to `validate` to avoid infinite register/unregister cycles. */
    repeatIfPasswordProvided: (passwordField: string = "password") => [
        isRequiredIfFieldExists(passwordField),
        validatePasswordRepeat(passwordField),
    ],
    repeatRequired: (passwordField: string = "password") => [
        isRequired,
        validatePasswordRepeat(passwordField),
    ],
}
